'use client';

/**
 * WebTestimonialCard — single italic blockquote + attribution.
 *
 * Ports klyde-website/src/pages/ForBuyers.tsx testimonial section.
 * Centred narrow column, dark section feel. Content via
 * `web_for_buyers_testimonial` strategy.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, XCircle } from 'lucide-react';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { api } from '@/lib/api/client';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import type { WebTestimonialContent } from '@/lib/api/types';
import { useLanguageStore } from '@/lib/stores/language-store';


interface WebTestimonialCardProps {
  strategyAction?: string;
}

export function WebTestimonialCard({
  strategyAction = 'web_for_buyers_testimonial',
}: WebTestimonialCardProps = {}) {
  const T = useCardTypography();
  const strategy = useCapabilityStore(
    (s) => s.strategyName('content', strategyAction),
  );
  const lang = useLanguageStore((s) => s.lang) ?? undefined;

  const [data, setData] = useState<WebTestimonialContent | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!strategy) return;
    let cancelled = false;
    api
      .getStrategyData<WebTestimonialContent>(strategy, lang ? { lang } : {})
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || 'Failed to load testimonial');
          return;
        }
        setData(res.data ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? 'Network error');
      });
    return () => { cancelled = true; };
  }, [strategy, lang]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-destructive">
        <XCircle className="h-6 w-6 mb-2" />
        <p className={T.body}>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading…
      </div>
    );
  }

  return (
    <section className="web-surface relative py-24 px-6 bg-background text-foreground overflow-hidden">
      <motion.div
        className="container mx-auto max-w-3xl text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <blockquote className="text-xl md:text-2xl font-medium leading-relaxed italic text-foreground/90 mb-6">
          “{data.quote}”
        </blockquote>
        <p className={cn(T.metadata)}>— {data.attribution}</p>
        {data.tagline && (
          <p className="text-xl md:text-2xl font-bold text-primary mt-8">
            {data.tagline}
          </p>
        )}
      </motion.div>
    </section>
  );
}
